import { ICellRendererParams } from 'ag-grid-community';
import React, { useState } from 'react';
import styled from 'styled-components';
import * as Colors from '../../common/colors';
import { AnnotationTableRow } from '../models/annotation-table-row.model';
import { AnnotationDiffEntry, charDiff } from '../models/annotation-diff.model';
import {
  CheckCircleTwoTone,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  ExclamationCircleTwoTone,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { AppState } from '../../app-state';
import {
  selectAnnotationValidations,
  selectAnnotationValues,
} from '../selectors/annotation.selectors';
import { selectVersionDiff } from '../selectors/version-history.selectors';

const ShowMoreToggle = styled.div`
  position: absolute;
  right: 0px;
  top: 0px;
  color: ${Colors.AnchorColor};
  text-decoration: underline;
  cursor: pointer;
  padding: 0 6px;
  background-color: rgba(236, 240, 241, 0.9);
  padding-top: 4px;

  &:hover {
    color: ${Colors.ActiveLinkColor};
  }
`;

const ShowLessToggle = styled(ShowMoreToggle)``;

const RightIcon = styled(DoubleRightOutlined)`
  margin-left: 4px;
`;
const LeftIcon = styled(DoubleLeftOutlined)`
  margin-right: 4px;
`;

const DiffInsert = styled.span`
  background-color: #dfd;
  color: #080;
`;

const DiffDelete = styled.span`
  background-color: #fdd;
  color: #a00;
  text-decoration: line-through;
`;

function InlineDiff({
  oldValue,
  newValue,
}: {
  oldValue: string;
  newValue: string;
}) {
  const segments = charDiff(oldValue, newValue);
  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'insert')
          return <DiffInsert key={i}>{seg.text}</DiffInsert>;
        if (seg.type === 'delete')
          return <DiffDelete key={i}>{seg.text}</DiffDelete>;
        return <span key={i}>{seg.text}</span>;
      })}
    </>
  );
}

export type ValueCellProps = {
  expandCell: (id: string) => void;
  unexpandCell: (id: string) => void;
  annotationValue: string;
  annotationValidation: boolean | undefined;
  diffEntry: AnnotationDiffEntry | undefined;
} & ICellRendererParams;

export const ValueCell = React.memo((props: ValueCellProps) => {
  const rowData = props.data as AnnotationTableRow;
  const atnId = rowData.annotationId;
  const annotationValue = props.annotationValue;
  const annotationIsValid = props.annotationValidation;
  const diffEntry = props.diffEntry;
  const initExpansion = props.node.rowHeight > 30;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [isExpanded, setIsExpanded] = useState(initExpansion);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mouseOver, setMouseOver] = useState(false);

  const width = props.eGridCell.offsetWidth;
  const lines = AnnotationTableRow.valueCellLineCount(rowData, width);

  const expandedStyle = {
    whiteSpace: 'normal' as const,
    lineHeight: '18px',
    paddingTop: '4px',
  };

  const unexpandedStyle = {
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    lineHeight: '18px',
    paddingTop: '4px',
  };

  const onShowMoreClick = () => {
    setIsExpanded(true);
    props.expandCell(atnId);
  };

  const onShowLessClick = () => {
    setIsExpanded(false);
    props.unexpandCell(atnId);
  };

  const validationIcon = () => {
    switch (annotationIsValid) {
      case true:
        return <CheckCircleTwoTone twoToneColor={Colors.LightGreen} />;
      case false:
        return <ExclamationCircleTwoTone twoToneColor={Colors.LightRed} />;
      default:
        return <QuestionCircleOutlined />;
    }
  };

  const showInlineDiff =
    diffEntry && diffEntry.status === 'changed' && diffEntry.oldValue != null;

  return (
    <div
      style={isExpanded ? expandedStyle : unexpandedStyle}
      onMouseOver={() => setMouseOver(true)}
      onMouseOut={() => setMouseOver(false)}
    >
      {validationIcon()}{' '}
      {showInlineDiff ? (
        <InlineDiff
          oldValue={diffEntry!.oldValue!}
          newValue={annotationValue}
        />
      ) : (
        annotationValue
      )}
      {lines > 1 && !isExpanded && (
        <ShowMoreToggle
          style={{ display: mouseOver ? 'inherit' : 'none' }}
          onClick={onShowMoreClick}
        >
          Show More
          <RightIcon />
        </ShowMoreToggle>
      )}
      {lines > 1 && isExpanded && (
        <ShowLessToggle
          style={{ display: mouseOver ? 'inherit' : 'none' }}
          onClick={onShowLessClick}
        >
          <LeftIcon />
          Show Less
        </ShowLessToggle>
      )}
    </div>
  );
});

export default connect(
  (state: AppState, ownProps: ICellRendererParams) => {
    const atnId = ownProps?.data?.annotationId || '';
    const diffResult = selectVersionDiff(state);
    return {
      annotationValue:
        selectAnnotationValues(state).get(atnId) ||
        ownProps?.data?.annotation?.value ||
        '',
      annotationValidation: selectAnnotationValidations(state).get(atnId),
      diffEntry: diffResult.entries[atnId],
    };
  },
  dispatch => ({}),
)(ValueCell);
